import { createTwoFilesPatch } from "diff";
import type { AssistantToolDef } from "@ide/shared/assistant";
import { desktop } from "@/desktop/bridge";
import { get } from "@/lib/api";
import { queryClient } from "@/lib/queries";
import type { WorkspaceContextValue } from "@/workspace/context";
import { deleteFile, readFile, writeFile } from "@/workspace/file-ops";
import {
  commitAndPush,
  commitFiles,
  computeChanges,
  createRepo,
  headCommit,
  readProjectTexts,
  repoLabel,
  resolveRepoInput,
  shasOf,
  type GitLink,
} from "@/workspace/git";
import { gitKeys, saveLink } from "@/workspace/git-panel";
import { flushLocalCopy, useLocalCopy } from "@/workspace/local-copy";
import { runInTerminal } from "@/workspace/terminal-panel";

/**
 * Ferramentas do agente (ADR 0025). Cada uma tem duas fases: `prepare` confere a entrada e monta
 * o que a pessoa vai aprovar (o diff, o comando); `run` executa depois da permissão. Mudanças nos
 * arquivos entram como edição normal da pessoa (histórico, autoria, o time vê na hora).
 */

export type ToolKind = "read" | "edit" | "delete" | "command" | "git";

export interface ToolContext {
  ws: WorkspaceContextValue;
  signal: AbortSignal;
  requestId: string;
  onOutput(text: string): void;
}

export interface Prepared {
  /** Uma linha para a lista de passos ("Editar src/app.ts"). */
  title: string;
  /** Diff unificado de um arquivo (edições). */
  patch?: string;
  path?: string;
  /** Comando que vai rodar. */
  command?: string;
  /** Detalhe extra para a permissão (ex.: arquivos do commit). */
  detail?: string;
  run(): Promise<ToolOutcome>;
}

export interface ToolOutcome {
  content: string;
  isError?: boolean;
  /** Resumo curto para a interface ("12 linhas", "código 0"). */
  note?: string;
}

interface ToolSpec {
  name: string;
  kind: ToolKind;
  description: string;
  schema: { type: "object"; properties: Record<string, Prop>; required?: string[] };
  /** Só no app (precisa da cópia local, do GitHub…). */
  desktopOnly?: boolean;
  prepare(input: Record<string, unknown>, ctx: ToolContext): Promise<Prepared>;
}

type Prop =
  | { type: "string"; description: string; maxLength?: number }
  | { type: "integer"; description: string; minimum?: number; maximum?: number }
  | { type: "boolean"; description: string }
  | { type: "array"; description: string; items: { type: "string" }; maxItems?: number };

export class ToolInputError extends Error {}

/** Confere a entrada contra o esquema da própria ferramenta (a entrada vem do modelo). */
export function checkInput(spec: Pick<ToolSpec, "schema">, input: Record<string, unknown>): void {
  if ("__invalid" in input)
    throw new ToolInputError("A entrada da ferramenta veio cortada ou não é JSON válido.");
  for (const key of spec.schema.required ?? []) {
    if (input[key] === undefined || input[key] === null) throw new ToolInputError(`Falta o campo "${key}".`);
  }
  for (const [key, value] of Object.entries(input)) {
    const prop = spec.schema.properties[key];
    if (!prop) throw new ToolInputError(`Campo desconhecido: "${key}".`);
    if (value === undefined || value === null) continue;
    if (prop.type === "string") {
      if (typeof value !== "string") throw new ToolInputError(`"${key}" deve ser texto.`);
      if (prop.maxLength && value.length > prop.maxLength)
        throw new ToolInputError(`"${key}" é grande demais.`);
    } else if (prop.type === "integer") {
      if (typeof value !== "number" || !Number.isInteger(value))
        throw new ToolInputError(`"${key}" deve ser um número inteiro.`);
      if (
        (prop.minimum !== undefined && value < prop.minimum) ||
        (prop.maximum !== undefined && value > prop.maximum)
      )
        throw new ToolInputError(`"${key}" está fora do intervalo.`);
    } else if (prop.type === "boolean") {
      if (typeof value !== "boolean") throw new ToolInputError(`"${key}" deve ser true ou false.`);
    } else if (
      !Array.isArray(value) ||
      !value.every((v) => typeof v === "string") ||
      (prop.maxItems && value.length > prop.maxItems)
    ) {
      throw new ToolInputError(`"${key}" deve ser uma lista de textos.`);
    }
  }
}

const MAX_READ = 120_000;
const MAX_WRITE = 1_000_000;

/** Arquivos de segredo (.env, chaves): o agente não lê nem procura dentro deles. */
export function isSecretPath(path: string): boolean {
  const name = path.split("/").pop() ?? "";
  if (/^\.env(\..+)?$/i.test(name)) return !/\.(example|sample|template|dist)$/i.test(name);
  return /\.(pem|key|p12|pfx|keystore|jks)$/i.test(name) || /^id_(rsa|ed25519|ecdsa)$/i.test(name);
}

/** Caminho relativo do projeto (sem "..", sem barra no começo, nada de pastas do Git). */
export function cleanPath(raw: string): string {
  const path = raw
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .replace(/\/+$/, "");
  const parts = path.split("/");
  if (
    !path ||
    path.length > 400 ||
    path.startsWith("/") ||
    /^[a-z]:/i.test(path) ||
    parts.some((p) => !p || p === "." || p === ".." || p === ".git") ||
    [...path].some((c) => c.charCodeAt(0) < 32 || '<>:"|?*'.includes(c))
  ) {
    throw new ToolInputError(`Caminho inválido: "${raw}". Use caminhos relativos à raiz do projeto.`);
  }
  return path;
}

const patchOf = (path: string, before: string | null, after: string | null) =>
  createTwoFilesPatch(path, path, before ?? "", after ?? "", "", "", { context: 3 });

const lines = (text: string) => (text ? text.split("\n").length : 0);

function countOf(haystack: string, needle: string) {
  let n = 0;
  for (let i = haystack.indexOf(needle); i >= 0; i = haystack.indexOf(needle, i + needle.length)) n++;
  return n;
}

async function gitLink(ws: WorkspaceContextValue): Promise<GitLink | null> {
  return get<{ link: GitLink | null }>(`/projects/${ws.detail.project.id}/git`).then((r) => r.link);
}

const author = (ws: WorkspaceContextValue) => ({
  name: ws.me.user.name,
  email: ws.me.user.email ?? `${ws.me.user.handle}@users.noreply.rrmind.tech`,
});

function requireWrite(ws: WorkspaceContextValue) {
  if (!ws.session.canWrite) throw new ToolInputError("Seu papel neste projeto não permite alterar arquivos.");
}

const localCopyOn = (ws: WorkspaceContextValue) => {
  const s = useLocalCopy.getState();
  return s.projectId === ws.detail.project.id && s.state === "on";
};

const LOCAL_OFF =
  'A cópia local está desligada. Peça à pessoa para ligar "Salvar na máquina" (canto superior do projeto) para rodar comandos no computador dela.';

export const TOOLS: ToolSpec[] = [
  {
    name: "list_files",
    kind: "read",
    description:
      "Lista os arquivos do projeto (caminhos relativos à raiz). Use `path` para ver só uma pasta. Arquivos de segredo aparecem marcados e não podem ser lidos.",
    schema: {
      type: "object",
      properties: { path: { type: "string", description: "Pasta (opcional), ex.: src/components" } },
    },
    async prepare(input, { ws }) {
      const prefix = typeof input.path === "string" && input.path.trim() ? `${cleanPath(input.path)}/` : "";
      const all = [...ws.session.tree().paths.values()].filter((p) => p.startsWith(prefix)).sort();
      return {
        title: prefix ? `Listar ${prefix}` : "Listar arquivos do projeto",
        run: async () => ({
          content: all.length
            ? all
                .slice(0, 3000)
                .map((p) => (isSecretPath(p) ? `${p} (segredo, protegido)` : p))
                .join("\n") + (all.length > 3000 ? `\n… e mais ${all.length - 3000}` : "")
            : "Nenhum arquivo aqui.",
          note: `${all.length} ${all.length === 1 ? "arquivo" : "arquivos"}`,
        }),
      };
    },
  },
  {
    name: "read_file",
    kind: "read",
    description:
      "Lê um arquivo de texto do projeto. Para arquivos grandes, peça um trecho com start_line/end_line (1 = primeira linha). Leia antes de editar.",
    schema: {
      type: "object",
      properties: {
        path: { type: "string", description: "Caminho do arquivo, ex.: src/index.ts" },
        start_line: { type: "integer", description: "Primeira linha (opcional)", minimum: 1 },
        end_line: { type: "integer", description: "Última linha (opcional)", minimum: 1 },
      },
      required: ["path"],
    },
    async prepare(input, { ws }) {
      const path = cleanPath(String(input.path));
      if (isSecretPath(path)) throw new ToolInputError(`${path} guarda segredos e não é enviado para a IA.`);
      return {
        title: `Ler ${path}`,
        path,
        run: async () => {
          const text = await readFile(ws.session, path);
          if (text === null) return { content: `O arquivo ${path} não existe.`, isError: true };
          const all = text.split("\n");
          const start = typeof input.start_line === "number" ? input.start_line : 1;
          const end = typeof input.end_line === "number" ? Math.min(input.end_line, all.length) : all.length;
          let body = all.slice(start - 1, end).join("\n");
          let cut = "";
          if (body.length > MAX_READ) {
            body = body.slice(0, MAX_READ);
            cut = `\n\n[cortado: o arquivo tem ${all.length} linhas; peça um trecho com start_line/end_line]`;
          }
          const range = start > 1 || end < all.length ? `(linhas ${start}–${end} de ${all.length})\n` : "";
          return { content: range + body + cut, note: `${end - start + 1} linhas` };
        },
      };
    },
  },
  {
    name: "search",
    kind: "read",
    description:
      "Procura um texto (ou expressão regular) em todos os arquivos do projeto. Devolve caminho:linha: trecho. Use para achar onde algo é definido ou usado.",
    schema: {
      type: "object",
      properties: {
        query: { type: "string", description: "Texto ou regex", maxLength: 500 },
        regex: { type: "boolean", description: "Tratar `query` como expressão regular" },
        path: { type: "string", description: "Só nesta pasta (opcional)" },
      },
      required: ["query"],
    },
    async prepare(input, { ws }) {
      const query = String(input.query);
      if (!query) throw new ToolInputError("Informe o que procurar.");
      let re: RegExp;
      try {
        re = input.regex
          ? new RegExp(query, "i")
          : new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
      } catch {
        throw new ToolInputError("Expressão regular inválida.");
      }
      const prefix = typeof input.path === "string" && input.path.trim() ? `${cleanPath(input.path)}/` : "";
      return {
        title: `Procurar “${query.slice(0, 60)}”`,
        run: async () => {
          const texts = await readProjectTexts(ws.session);
          const hits: string[] = [];
          for (const [path, text] of Object.entries(texts).sort(([a], [b]) => a.localeCompare(b))) {
            if (!path.startsWith(prefix) || isSecretPath(path)) continue;
            const ls = text.split("\n");
            for (let i = 0; i < ls.length && hits.length < 200; i++) {
              if (re.test(ls[i]!)) hits.push(`${path}:${i + 1}: ${ls[i]!.trim().slice(0, 200)}`);
            }
            if (hits.length >= 200) break;
          }
          return {
            content: hits.length ? hits.join("\n") : "Nada encontrado.",
            note: `${hits.length} resultados`,
          };
        },
      };
    },
  },
  {
    name: "write_file",
    kind: "edit",
    description:
      "Cria um arquivo ou troca o conteúdo inteiro de um existente (as pastas são criadas). Para mudanças pequenas em arquivo existente, prefira edit_file.",
    schema: {
      type: "object",
      properties: {
        path: { type: "string", description: "Caminho do arquivo" },
        content: { type: "string", description: "Conteúdo completo do arquivo", maxLength: MAX_WRITE },
      },
      required: ["path", "content"],
    },
    async prepare(input, { ws }) {
      requireWrite(ws);
      const path = cleanPath(String(input.path));
      const content = String(input.content);
      const before = await readFile(ws.session, path);
      if (before === content) throw new ToolInputError(`${path} já tem exatamente esse conteúdo.`);
      return {
        title: before === null ? `Criar ${path}` : `Reescrever ${path}`,
        path,
        patch: patchOf(path, before, content),
        run: async () => {
          const r = await writeFile(ws.session, path, content);
          return {
            content: r === "created" ? `Arquivo ${path} criado.` : `Arquivo ${path} atualizado.`,
            note: `${lines(content)} linhas`,
          };
        },
      };
    },
  },
  {
    name: "edit_file",
    kind: "edit",
    description:
      "Troca um trecho exato de um arquivo por outro. `old_string` precisa aparecer exatamente uma vez (inclua linhas vizinhas para ficar único) ou use replace_all. Respeite a indentação original.",
    schema: {
      type: "object",
      properties: {
        path: { type: "string", description: "Caminho do arquivo" },
        old_string: {
          type: "string",
          description: "Trecho atual, exatamente como está",
          maxLength: MAX_WRITE,
        },
        new_string: { type: "string", description: "Trecho novo", maxLength: MAX_WRITE },
        replace_all: { type: "boolean", description: "Trocar todas as ocorrências" },
      },
      required: ["path", "old_string", "new_string"],
    },
    async prepare(input, { ws }) {
      requireWrite(ws);
      const path = cleanPath(String(input.path));
      const oldStr = String(input.old_string);
      const newStr = String(input.new_string);
      if (!oldStr) throw new ToolInputError("old_string está vazio; para criar um arquivo use write_file.");
      if (oldStr === newStr) throw new ToolInputError("old_string e new_string são iguais.");
      const apply = (text: string) => {
        const n = countOf(text, oldStr);
        if (!n)
          throw new ToolInputError(
            `O trecho não foi encontrado em ${path}. Leia o arquivo de novo e copie o trecho exato.`,
          );
        if (n > 1 && !input.replace_all)
          throw new ToolInputError(
            `O trecho aparece ${n} vezes em ${path}. Inclua mais contexto ou use replace_all.`,
          );
        return input.replace_all ? text.split(oldStr).join(newStr) : text.replace(oldStr, () => newStr);
      };
      const before = await readFile(ws.session, path);
      if (before === null) throw new ToolInputError(`O arquivo ${path} não existe.`);
      const after = apply(before);
      return {
        title: `Editar ${path}`,
        path,
        patch: patchOf(path, before, after),
        run: async () => {
          // Alguém pode ter mexido no arquivo enquanto a pessoa aprovava: aplica sobre o atual.
          const current = (await readFile(ws.session, path)) ?? "";
          const next = apply(current);
          await writeFile(ws.session, path, next);
          const added = newStr.split("\n").length;
          const removed = oldStr.split("\n").length;
          return { content: `Arquivo ${path} editado.`, note: `+${added} −${removed}` };
        },
      };
    },
  },
  {
    name: "delete_file",
    kind: "delete",
    description: "Apaga um arquivo do projeto (fica no histórico do Kompil e pode ser restaurado).",
    schema: {
      type: "object",
      properties: { path: { type: "string", description: "Caminho do arquivo" } },
      required: ["path"],
    },
    async prepare(input, { ws }) {
      requireWrite(ws);
      const path = cleanPath(String(input.path));
      const before = isSecretPath(path) ? "" : await readFile(ws.session, path);
      if (before === null) throw new ToolInputError(`O arquivo ${path} não existe.`);
      return {
        title: `Apagar ${path}`,
        path,
        ...(isSecretPath(path) ? {} : { patch: patchOf(path, before, null) }),
        run: async () =>
          deleteFile(ws.session, path)
            ? { content: `Arquivo ${path} apagado.` }
            : { content: `O arquivo ${path} não existe mais.`, isError: true },
      };
    },
  },
  {
    name: "run_command",
    kind: "command",
    desktopOnly: true,
    description:
      "Roda um comando no terminal do computador da pessoa, na pasta do projeto, e devolve a saída (o fim, até 40 mil caracteres) e o código de saída. Sem entrada interativa: use flags como --yes. Não use para servidores que ficam rodando (npm run dev, streamlit run…): para isso use start_in_terminal.",
    schema: {
      type: "object",
      properties: {
        command: {
          type: "string",
          description: "Comando (PowerShell no Windows; o shell da pessoa no macOS/Linux)",
          maxLength: 4000,
        },
        timeout_seconds: {
          type: "integer",
          description: "Tempo máximo (padrão 120, até 900)",
          minimum: 1,
          maximum: 900,
        },
      },
      required: ["command"],
    },
    async prepare(input, ctx) {
      const command = String(input.command).trim();
      if (!command) throw new ToolInputError("Informe o comando.");
      if (!desktop?.assistant) throw new ToolInputError("Comandos só rodam no app do Kompil para desktop.");
      if (!localCopyOn(ctx.ws)) throw new ToolInputError(LOCAL_OFF);
      const timeoutMs = (typeof input.timeout_seconds === "number" ? input.timeout_seconds : 120) * 1000;
      return {
        title: `Rodar ${command.length > 80 ? `${command.slice(0, 80)}…` : command}`,
        command,
        run: async () => {
          // O disco precisa ter o que está no editor antes de rodar.
          flushLocalCopy();
          await new Promise((r) => setTimeout(r, 300));
          const off = desktop!.assistant.onEvent((e) => {
            if (e.type === "output" && e.requestId === ctx.requestId) ctx.onOutput(e.text);
          });
          const stop = () => desktop!.assistant.cancel(ctx.requestId);
          ctx.signal.addEventListener("abort", stop);
          try {
            const r = await desktop!.assistant.exec({
              requestId: ctx.requestId,
              projectId: ctx.ws.detail.project.id,
              command,
              timeoutMs,
            });
            // O que o comando criou na pasta volta para o projeto pela cópia local.
            await new Promise((res) => setTimeout(res, 900));
            if (r.error) return { content: r.error, isError: true };
            const status = r.cancelled
              ? "Cancelado pela pessoa."
              : r.timedOut
                ? `Passou de ${timeoutMs / 1000} s e foi encerrado.`
                : `Código de saída: ${r.exitCode}`;
            return {
              content: `${status}\n\n${r.output.trim() || "(sem saída)"}`,
              isError: r.cancelled || r.timedOut || r.exitCode !== 0,
              note: r.cancelled ? "cancelado" : r.timedOut ? "tempo esgotado" : `código ${r.exitCode}`,
            };
          } finally {
            off();
            ctx.signal.removeEventListener("abort", stop);
          }
        },
      };
    },
  },
  {
    name: "start_in_terminal",
    kind: "command",
    description:
      "Abre o terminal do Kompil e roda um comando que fica rodando (servidor de desenvolvimento, streamlit, watch). Não espera terminar nem devolve a saída. Sem comando, usa o Executar do projeto.",
    schema: {
      type: "object",
      properties: { command: { type: "string", description: "Comando (opcional)", maxLength: 4000 } },
    },
    async prepare(input, { ws }) {
      const command =
        typeof input.command === "string" && input.command.trim() ? input.command.trim() : undefined;
      return {
        title: command ? `Abrir no terminal: ${command.slice(0, 80)}` : "Executar o projeto",
        ...(command ? { command } : {}),
        run: async () => {
          await runInTerminal(ws.session, command);
          return { content: "Comando enviado ao terminal do Kompil; a pessoa acompanha a saída lá." };
        },
      };
    },
  },
  {
    name: "git_status",
    kind: "read",
    desktopOnly: true,
    description:
      "Mostra o repositório Git ligado ao projeto (se houver) e os arquivos alterados desde o último commit.",
    schema: { type: "object", properties: {} },
    async prepare(_input, { ws }) {
      return {
        title: "Ver o estado do Git",
        run: async () => {
          const link = await gitLink(ws);
          const account = await desktop?.git.status().catch(() => null);
          const gh = account?.connected
            ? `GitHub conectado como ${account.login ?? "?"}.`
            : "GitHub não conectado.";
          if (!link) return { content: `Nenhum repositório ligado a este projeto. ${gh}` };
          const changes = computeChanges(await shasOf(await readProjectTexts(ws.session)), link.baseFiles);
          return {
            content: `Repositório ${repoLabel(link.repo)}, branch ${link.branch}. ${gh}\n${
              changes.length
                ? changes.map((c) => `${c.kind} ${c.path}`).join("\n")
                : "Nada alterado desde o último commit."
            }`,
            note: `${changes.length} alterações`,
          };
        },
      };
    },
  },
  {
    name: "git_commit_push",
    kind: "git",
    desktopOnly: true,
    description:
      "Faz commit e push das alterações na branch do repositório ligado (só avança, nunca reescreve o histórico). `paths` limita o commit a esses arquivos.",
    schema: {
      type: "object",
      properties: {
        message: {
          type: "string",
          description: "Mensagem do commit (no idioma do projeto)",
          maxLength: 2000,
        },
        paths: {
          type: "array",
          description: "Só estes arquivos (opcional)",
          items: { type: "string" },
          maxItems: 1000,
        },
      },
      required: ["message"],
    },
    async prepare(input, { ws }) {
      const message = String(input.message).trim();
      if (!message) throw new ToolInputError("Escreva a mensagem do commit.");
      const link = await gitLink(ws);
      if (!link)
        throw new ToolInputError(
          "Este projeto não tem repositório. Use git_create_repository ou git_link_repository.",
        );
      const texts = await readProjectTexts(ws.session);
      const only =
        Array.isArray(input.paths) && input.paths.length
          ? new Set((input.paths as string[]).map(cleanPath))
          : undefined;
      const changes = computeChanges(await shasOf(texts), link.baseFiles).filter(
        (c) => !only || only.has(c.path),
      );
      if (!changes.length) throw new ToolInputError("Não há alterações para commitar.");
      return {
        title: `Commit e push em ${link.branch}: ${message.split("\n")[0]!.slice(0, 70)}`,
        detail: `${repoLabel(link.repo)} · ${changes
          .map((c) => `${c.kind} ${c.path}`)
          .slice(0, 30)
          .join(", ")}${changes.length > 30 ? "…" : ""}`,
        run: async () => {
          const fresh = await readProjectTexts(ws.session);
          const res = await commitAndPush(link, fresh, message, only, author(ws));
          await saveLink(ws.detail.project.id, { ...link, baseCommit: res.commit, baseFiles: res.baseFiles });
          void queryClient.invalidateQueries({ queryKey: gitKeys.link(ws.detail.project.id) });
          return {
            content: `Commit ${res.commit.slice(0, 7)} enviado para ${link.branch} (${res.changes.length} arquivos).`,
            note: res.commit.slice(0, 7),
          };
        },
      };
    },
  },
  {
    name: "git_create_repository",
    kind: "git",
    desktopOnly: true,
    description:
      "Cria um repositório novo no GitHub da pessoa e liga ao projeto (depois, use git_commit_push para enviar os arquivos).",
    schema: {
      type: "object",
      properties: {
        name: { type: "string", description: "Nome do repositório (letras, números, - _ .)", maxLength: 100 },
        private: { type: "boolean", description: "Privado (padrão: true)" },
      },
      required: ["name"],
    },
    async prepare(input, { ws }) {
      const name = String(input.name).trim();
      if (!/^[A-Za-z0-9_.-]{1,100}$/.test(name)) throw new ToolInputError("Nome de repositório inválido.");
      if (await gitLink(ws)) throw new ToolInputError("O projeto já tem um repositório ligado.");
      const account = await desktop?.git.status().catch(() => null);
      if (!account?.connected)
        throw new ToolInputError("Peça à pessoa para conectar o GitHub (painel Git) antes.");
      const priv = input.private !== false;
      return {
        title: `Criar o repositório ${priv ? "privado" : "público"} ${account.login ?? ""}/${name}`,
        run: async () => {
          const created = await createRepo(name, priv, ws.detail.project.description ?? undefined);
          await saveLink(ws.detail.project.id, created);
          void queryClient.invalidateQueries({ queryKey: gitKeys.link(ws.detail.project.id) });
          return {
            content: `Repositório ${created.repo} criado na branch ${created.branch} e ligado ao projeto.`,
          };
        },
      };
    },
  },
  {
    name: "git_link_repository",
    kind: "git",
    desktopOnly: true,
    description:
      "Liga o projeto a um repositório que já existe (dono/repo do GitHub ou URL https). Os arquivos do projeto ficam como estão; a diferença para o repositório vira alteração para commitar.",
    schema: {
      type: "object",
      properties: {
        repository: { type: "string", description: "dono/repo ou https://…", maxLength: 300 },
        branch: { type: "string", description: "Branch (padrão: main)", maxLength: 200 },
      },
      required: ["repository"],
    },
    async prepare(input, { ws }) {
      if (await gitLink(ws)) throw new ToolInputError("O projeto já tem um repositório ligado.");
      const account = await desktop?.git.status().catch(() => null);
      const full = await resolveRepoInput(String(input.repository), Boolean(account?.connected));
      if (!full)
        throw new ToolInputError("Use dono/repositório (GitHub) ou o endereço https do repositório.");
      const branch = (typeof input.branch === "string" && input.branch.trim()) || "main";
      return {
        title: `Ligar o projeto a ${repoLabel(full)} (${branch})`,
        run: async () => {
          const head = await headCommit(full, branch);
          if (!head)
            return { content: `A branch ${branch} não existe em ${repoLabel(full)}.`, isError: true };
          const files = await commitFiles(full, head, branch);
          await saveLink(ws.detail.project.id, {
            repo: full,
            branch,
            baseCommit: head,
            baseFiles: Object.fromEntries(Object.entries(files.files).map(([p, f]) => [p, f.sha])),
          });
          void queryClient.invalidateQueries({ queryKey: gitKeys.link(ws.detail.project.id) });
          return { content: `Projeto ligado a ${repoLabel(full)} na branch ${branch}.` };
        },
      };
    },
  },
];

export const toolByName = (name: string) => TOOLS.find((t) => t.name === name);

/** Definições enviadas ao modelo (só as que funcionam aqui). */
export function toolDefs(opts: { desktop: boolean }): AssistantToolDef[] {
  return TOOLS.filter((t) => opts.desktop || !t.desktopOnly).map((t) => ({
    name: t.name,
    description: t.description,
    inputSchema: t.schema,
  }));
}
